import axios from "@/lib/axios"

export type TaskKind = "practice" | "review" | "mock_test" | "note" | "custom"
export type TaskStatus = "pending" | "done" | "skipped"
export type Subject = "math" | "reading"

export interface PlannerSkill {
    name: string
    slug: string
}

export interface PlannerDomain {
    name: string
    subject: Subject
    skills: PlannerSkill[]
}

export interface StudyPlanTask {
    id: number
    plan_id: number
    scheduled_date: string // YYYY-MM-DD
    position: number
    kind: TaskKind
    subject?: Subject | null
    domain_name?: string | null
    skill_name?: string | null
    skill_slug?: string | null
    title?: string | null
    target_questions: number
    estimated_minutes: number
    status: TaskStatus
    note?: string | null
    is_custom: boolean
    /** Distinct questions actually attempted for this skill on this day.
     *  Computed server-side from practice attempts; not editable. */
    solved_questions: number
    created_at?: string
    updated_at?: string
}

export interface StudyPlanStats {
    total_tasks: number
    completed_tasks: number
    total_minutes: number
    study_days: number
    days_until_exam: number
    weeks_until_exam: number
    domain_distribution: Record<string, number>
}

export interface StudyPlan {
    id: number
    user_id: number
    target_score: number
    baseline_score?: number | null
    exam_date: string
    daily_minutes: number
    study_weekdays: number[]
    strengths: Record<string, number>
    is_active: boolean
    created_at?: string
    updated_at?: string
    tasks: StudyPlanTask[]
    stats?: StudyPlanStats
}

export interface GeneratePlanPayload {
    target_score: number
    exam_date: string
    daily_minutes: number
    study_weekdays: number[]
    strengths: Record<string, number>
}

export interface CreateTaskPayload {
    scheduled_date: string
    kind?: TaskKind
    subject?: Subject | null
    domain_name?: string | null
    skill_name?: string | null
    skill_slug?: string | null
    title?: string | null
    target_questions?: number
    estimated_minutes?: number
    note?: string | null
}

export type UpdateTaskPayload = Partial<
    Pick<
        StudyPlanTask,
        | "scheduled_date"
        | "kind"
        | "subject"
        | "domain_name"
        | "skill_name"
        | "skill_slug"
        | "title"
        | "target_questions"
        | "estimated_minutes"
        | "status"
        | "note"
        | "position"
    >
>

export const plannerService = {
    getTaxonomy: async (): Promise<PlannerDomain[]> => {
        const res = await axios.get("/planner/taxonomy")
        return res.data.domains
    },

    getMyPlan: async (): Promise<StudyPlan | null> => {
        const res = await axios.get("/planner/me")
        return res.data ?? null
    },

    generate: async (
        payload: GeneratePlanPayload,
        baselineScore?: number
    ): Promise<StudyPlan> => {
        const res = await axios.post("/planner/generate", payload, {
            params: baselineScore ? { baseline_score: baselineScore } : undefined,
        })
        return res.data
    },

    createTask: async (payload: CreateTaskPayload): Promise<StudyPlanTask> => {
        const res = await axios.post("/planner/tasks", payload)
        return res.data
    },

    updateTask: async (
        taskId: number,
        payload: UpdateTaskPayload
    ): Promise<StudyPlanTask> => {
        const res = await axios.patch(`/planner/tasks/${taskId}`, payload)
        return res.data
    },

    deleteTask: async (taskId: number): Promise<void> => {
        await axios.delete(`/planner/tasks/${taskId}`)
    },

    deletePlan: async (): Promise<void> => {
        await axios.delete("/planner/me")
    },
}
